import { useSearch } from "@tanstack/react-router"
import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import { useCallback } from "react"
import type { RefObject } from "react"
import type { Client, CombinedError } from "urql"
import { useShallow } from "zustand/react/shallow"

import { PENDING_APPROVALS_ROUTE, TaskViewScope } from "../constants"
import type { PriceApprovalSearchFilters } from "../stores/usePriceApprovalSearchStore"
import { usePriceApprovalSearchStore } from "../stores/usePriceApprovalSearchStore"

import type { PriceChangeSubmission } from "@/graphql/codegen/graphql"
import { useListPageBase } from "@/hooks/useListPageBase"
import { useCurrentUserStore } from "@/stores/currentUserStore"

interface UsePriceApprovalListReturn {
  priceApprovals: PriceChangeSubmission[]
  isFetching: boolean
  error: CombinedError | Error | undefined
  sorting: SortingState
  tableContainerRef: RefObject<HTMLDivElement>
  handleScroll: () => void
  handleSortingChange: OnChangeFn<SortingState>
}

const ROUTE = "/app/dashboard/_layout/pending-approvals/_layout/price"

export const usePriceApprovalList = (): UsePriceApprovalListReturn => {
  const currentUser = useCurrentUserStore((state) => state.currentUser)
  const { taskViewScope } = useSearch({ from: PENDING_APPROVALS_ROUTE })
  const assignedToId = taskViewScope === TaskViewScope.MyTasks ? currentUser?.userId : undefined

  const store = usePriceApprovalSearchStore(
    useShallow(({ data, fetching, error, executeSearch, loadMore, clearStore }) => ({
      data,
      fetching,
      error,
      executeSearch,
      loadMore,
      clearStore,
    }))
  )
  const { executeSearch: storeExecuteSearch, loadMore: storeLoadMore } = store

  const executeSearch = useCallback(
    (client: Client, params: PriceApprovalSearchFilters, force?: boolean) => {
      storeExecuteSearch(client, { ...params, assignedToId }, force)
    },
    [storeExecuteSearch, assignedToId]
  )

  const loadMore = useCallback(
    (client: Client, params: PriceApprovalSearchFilters) => {
      storeLoadMore(client, { ...params, assignedToId })
    },
    [storeLoadMore, assignedToId]
  )

  const {
    data: priceApprovals,
    isFetching,
    error,
    sorting,
    tableContainerRef,
    handleScroll,
    handleSortingChange,
  } = useListPageBase<PriceChangeSubmission, PriceApprovalSearchFilters>({
    route: ROUTE,
    store: { ...store, executeSearch, loadMore },
  })

  return {
    priceApprovals,
    isFetching,
    error,
    sorting,
    tableContainerRef,
    handleScroll,
    handleSortingChange,
  }
}
